import * as React from 'react'
import styled from '@emotion/styled'
import { Popper, PopperProps } from 'react-popper'
import { Primary, shadows, spacing } from '@sphinx/components-styles'
import { ThemeProvider } from '@emotion/react'

export enum Placement {
  autoStart = 'auto-start',
  auto = 'auto',
  autoEnd = 'auto-end',
  topStart = 'top-start',
  top = 'top',
  topEnd = 'top-end',
  rightStart = 'right-start',
  right = 'right',
  rightEnd = 'right-end',
  bottomEnd = 'bottom-end',
  bottom = 'bottom',
  bottomStart = 'bottom-start',
  leftEnd = 'left-end',
  left = 'left',
  leftStart = 'left-start',
}

export enum TooltipTypes {
  DEFAULT = 'defaultTheme',
  WHITE = 'whiteTheme',
}

interface Theme {
  background: string
  color: string
}

interface TooltipProps extends React.HTMLAttributes<HTMLDivElement> {
  placement: Placement
  referenceElement: React.RefObject<any>
  type?: TooltipTypes
  modifiers?: Partial<PopperProps['modifiers']>
}

interface ThemableTooltipWrap extends React.HTMLAttributes<HTMLDivElement> {
  theme?: Theme
}

const tooltipTheme: { [index in TooltipTypes]: Theme } = {
  [TooltipTypes.DEFAULT]: {
    background: Primary.navy[5],
    color: Primary.neutral['black'],
  },
  [TooltipTypes.WHITE]: {
    background: Primary.neutral['white'],
    color: Primary.navy[100],
  },
}

const TooltipContent = styled.div`
  min-height: 25px;
  min-width: 40px;
  border-radius: 2px;
`

const Arrow = styled.div`
  width: 13px;
  height: 13px;
  position: absolute;
  z-index: -1;
  box-shadow: ${shadows.level8};
  transform: rotate(-45deg);

  &[data-placement*=${Placement.bottom}],
  &[data-placement*=${Placement.bottomEnd}],
  &[data-placement*=${Placement.bottomStart}] {
    top: -6px;
  }
  &[data-placement*=${Placement.top}],
  &[data-placement*=${Placement.topEnd}],
  &[data-placement*=${Placement.topStart}] {
    bottom: -6px;
  }
  &[data-placement*=${Placement.right}],
  &[data-placement*=${Placement.rightEnd}],
  &[data-placement*=${Placement.rightStart}] {
    left: -6px;
  }
  &[data-placement*=${Placement.left}],
  &[data-placement*=${Placement.leftEnd}],
  &[data-placement*=${Placement.leftStart}] {
    right: -6px;
  }
`

const TooltipWrap = styled.div<ThemableTooltipWrap>`
  box-shadow: ${shadows.level8};
  border-radius: 2px;

  &[data-placement*=${Placement.bottom}],
  &[data-placement*=${Placement.bottomEnd}],
  &[data-placement*=${Placement.bottomStart}] {
    margin-top: 10px;
  }
  &[data-placement*=${Placement.top}],
  &[data-placement*=${Placement.topEnd}],
  &[data-placement*=${Placement.topStart}] {
    margin-bottom: 10px;
  }
  &[data-placement*=${Placement.right}],
  &[data-placement*=${Placement.rightEnd}],
  &[data-placement*=${Placement.rightStart}] {
    margin-left: 10px;
  }
  &[data-placement*=${Placement.left}],
  &[data-placement*=${Placement.leftEnd}],
  &[data-placement*=${Placement.leftStart}] {
    margin-right: 10px;
  }

  & footer {
    margin: 0 ${spacing.xs};
    padding: 6px 0;
    border-top-color: ${Primary.navy[10]};
    min-height: 46px;
    box-sizing: border-box;
  }

  & > ${TooltipContent}, & > ${Arrow} {
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.color};
  }
`

export const Tooltip: React.FC<TooltipProps> = ({
  placement: propsPlacement,
  children,
  referenceElement,
  style: tooltipStyle,
  type = TooltipTypes.DEFAULT,
  modifiers,
  ...props
}) => (
  <Popper
    modifiers={{
      flip: {
        behavior: [
          Placement.left,
          Placement.right,
          Placement.top,
          Placement.bottom,
        ],
      },
      ...modifiers,
    }}
    positionFixed
    placement={propsPlacement}
    referenceElement={referenceElement && referenceElement.current}
  >
    {({ ref, style, arrowProps, placement }) => (
      <ThemeProvider theme={tooltipTheme[type]}>
        <TooltipWrap
          {...props}
          data-placement={placement}
          style={{ ...style, ...tooltipStyle }}
          ref={ref}
        >
          <TooltipContent>{children}</TooltipContent>
          <Arrow
            data-placement={placement}
            ref={arrowProps.ref}
            style={arrowProps.style}
          />
        </TooltipWrap>
      </ThemeProvider>
    )}
  </Popper>
)
